import { useEffect, useLayoutEffect, useState } from "react";
import Cookies from "js-cookie";
import type { IUser } from "@dietdoctor/elements";
import { getLocalUserInfo, isValidToken } from "../../utils/auth";
import { isBrowser } from "../../utils/ssr";

type NavMembership = IUser["membershipSubscription"];

const UNKNOWN_MEMBERSHIP: NavMembership = {
  status: "UNKNOWN" as NavMembership["status"],
  provider: "UNKNOWN" as NavMembership["provider"],
};

/**
 * Read the nav's user optimistically from the local session, so a signed-in member
 * does not see the anonymous nav while the authoritative auth check resolves.
 *
 * The local user info survives a session that died elsewhere (it is only cleared on an
 * explicit logout), so it is trusted only while the `auth` cookie holds an unexpired
 * JWT. That check is local and synchronous. The authoritative check still runs and
 * reconciles; this value is a first-paint guess, never an authorisation decision.
 */
export const getOptimisticNavUser = (): IUser | undefined => {
  const token = Cookies.get("auth");
  if (!token || !isValidToken(token)) {
    return undefined;
  }

  const userInfo = getLocalUserInfo();
  if (!userInfo) {
    return undefined;
  }

  const subscription = userInfo.membershipSubscription;

  return {
    firstName: userInfo.me.firstName,
    lastName: userInfo.me.lastName,
    membershipSubscription: subscription
      ? {
          status: subscription.status as unknown as NavMembership["status"],
          provider: subscription.provider as unknown as NavMembership["provider"],
        }
      : UNKNOWN_MEMBERSHIP,
  };
};

/**
 * `useLayoutEffect` does nothing on the server and warns if called there, so fall back to
 * `useEffect` when there is no DOM.
 */
const useIsomorphicLayoutEffect = isBrowser() ? useLayoutEffect : useEffect;

/**
 * The optimistic nav user, applied in a second pass.
 *
 * The first render deliberately returns `undefined`: the page is served with anonymous nav
 * markup (Gatsby SSRs it at build time, when no user exists), so returning a user on the
 * first render would be a hydration mismatch. The value is committed in a layout effect
 * instead, which runs after hydration but *before the browser paints* — so a member never
 * sees an anonymous frame once the bundle has run.
 */
export const useOptimisticNavUser = (): IUser | undefined => {
  const [user, setUser] = useState<IUser | undefined>(undefined);

  useIsomorphicLayoutEffect(() => {
    setUser(getOptimisticNavUser());
  }, []);

  return user;
};
